// Cache-busting trigger for Lovable deployment
import DOMPurify from "dompurify";
import { useState, useMemo, lazy, Suspense } from "react";
import { ArrowRight, Flame, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import SectionErrorBoundary from "@/components/SectionErrorBoundary";
import Navbar from "@/components/Navbar";
import FadeInSection from "@/components/FadeInSection";
import SEO from "@/components/SEO";
import { useSiteSettingsContext } from "@/contexts/SiteSettingsContext";
import { useArticles } from "@/contexts/ArticlesContext";
import type { Article } from "@/components/NewsSection";
import { getCategoryColor } from "@/lib/categoryColors";
import { useLiveMarkets } from "@/hooks/useLiveMarkets";
import { useHomepageConfig, usePodcastEpisodes, usePinnedStories, useFeatureModules } from "@/hooks/useHomepageConfig";
import { urlFor } from "@/lib/sanity";
import { useCategoryInterest } from "@/hooks/useCategoryInterest";
import { useHomepageOrder } from "@/hooks/useHomepageOrder";
import { useSeoOverride } from "@/hooks/useSeoOverride";
import { useTTS } from "@/hooks/useTTS";

import {
  TTSContext,
  SectionLabel,
  HeroSection,
  LatestNewsSection,
} from "@/features/homepage";

const TrendingMarketsSection = lazy(() => import("@/features/homepage/TrendingMarketsSection").then(m => ({ default: m.TrendingMarketsSection })));
const CategoryColumnsSection = lazy(() => import("@/features/homepage/CategoryColumnsSection").then(m => ({ default: m.CategoryColumnsSection })));
const RecommendedFeed = lazy(() => import("@/features/homepage/RecommendedFeed").then(m => ({ default: m.RecommendedFeed })));
const PodcastSection = lazy(() => import("@/features/homepage/PodcastSection").then(m => ({ default: m.PodcastSection })));
const SummitBanner = lazy(() => import("@/features/homepage/SummitBanner").then(m => ({ default: m.SummitBanner })));
const NewsletterSection = lazy(() => import("@/features/homepage/NewsletterSection").then(m => ({ default: m.NewsletterSection })));
const Footer = lazy(() => import("@/features/homepage/Footer").then(m => ({ default: m.Footer })));

const Index = () => {
  const tts = useTTS();
  const [activeCategory, setActiveCategory] = useState("Latest");
  const { isSectionVisible } = useSiteSettingsContext();
  const { data: marketData, isLoading: marketsLoading } = useLiveMarkets(10);
  const markets = marketData?.markets ?? [];
  const { articles: liveArticles, isLoading: articlesLoading } = useArticles();
  const { data: homepageConfig } = useHomepageConfig();
  const { data: podcastEpisodesData } = usePodcastEpisodes();
  const { data: sanityFeatureModules } = useFeatureModules();
  const pinnedStories = usePinnedStories(homepageConfig);
  const { sortCategories, sortByCategory } = useCategoryInterest();
  const { data: sectionOrder } = useHomepageOrder();
  const { data: seoOverride } = useSeoOverride("/");
  const articlesSource = liveArticles;

  const hasPinnedStories = pinnedStories.length > 0;

  const filtered = useMemo(() => {
    if (activeCategory === "Latest" || activeCategory === "Trending") return articlesSource;
    return articlesSource.filter((a) => a.category === activeCategory);
  }, [activeCategory, articlesSource]);

  const lead = hasPinnedStories ? pinnedStories[0] : filtered[0];
  const topStories = hasPinnedStories ? pinnedStories.slice(1, 6) : filtered.slice(1, 6);

  const categoryColumns = useMemo(() => {
    const seen = new Set<string>();
    const catLabels: Record<string, string> = { Financial: "Finance" };
    const UNIFORM_HEIGHT = 2;

    const cols = articlesSource.reduce<
      { category: string; label: string; href: string; stories: typeof articlesSource }[]
    >((acc, article) => {
      const cat = article.category;
      if (!seen.has(cat)) {
        seen.add(cat);
        acc.push({
          category: cat,
          label: catLabels[cat] ?? cat,
          href: `/news?cat=${encodeURIComponent(cat)}`,
          stories: articlesSource.filter((a) => a.category === cat),
        });
      }
      return acc;
    }, []);

    const sortedCols = sortByCategory(cols);
    return sortedCols
      .filter((col) => col.stories.length >= UNIFORM_HEIGHT)
      .map((col) => ({ ...col, stories: col.stories.slice(0, UNIFORM_HEIGHT) }))
      .slice(0, 4);
  }, [articlesSource, sortByCategory]);

  const featureArticles = useMemo(() => {
    const sanityPicks = (sanityFeatureModules ?? [])
      .filter((m) => m.article)
      .map((m) => {
        const a = m.article!;
        return {
          id: Date.parse(a.publishedAt ?? "") || Math.random(),
          slug: a.slug?.current ?? "",
          title: a.title,
          excerpt: a.excerpt ?? "",
          category: a.category?.title ?? "General",
          date: a.publishedAt ?? "",
          readTime: a.readTime ?? "",
          image: a.mainImage ? urlFor(a.mainImage).width(800).height(450).url() : "",
          tag: a.tag as any,
          author: a.author
            ? {
                name: a.author.name,
                slug: a.author.slug?.current ?? "",
                role: a.author.role,
                avatar: a.author.avatar ? urlFor(a.author.avatar).width(80).height(80).url() : undefined,
              }
            : undefined,
        } as Article;
      });
    if (sanityPicks.length > 0) return sanityPicks;

    const withTime = articlesSource
      .filter((a) => a.readTime)
      .map((a) => ({ article: a, mins: parseInt(a.readTime) || 0 }))
      .sort((a, b) => b.mins - a.mins);
    return withTime.slice(0, 5).map((w) => w.article);
  }, [articlesSource, sanityFeatureModules]);

  const politicsMarkets = markets.filter((m) => m.category === "Politics").slice(0, 1);
  const featuredConfig = homepageConfig?.featuredSection;

  const defaultOrder: {
    section_key: string;
    is_visible: boolean;
    sort_order: number;
    starts_at?: string | null;
    ends_at?: string | null;
  }[] = [
    { section_key: "hero", is_visible: true, sort_order: 0 },
    { section_key: "latest_news", is_visible: true, sort_order: 1 },
    { section_key: "category_columns", is_visible: true, sort_order: 2 },
    { section_key: "newsletter_banner", is_visible: true, sort_order: 3 },
    { section_key: "platform_section", is_visible: true, sort_order: 4 },
  ];
  const orderedSections = sectionOrder && sectionOrder.length > 0 ? sectionOrder : defaultOrder;

  const sanityPodcastEpisodes = podcastEpisodesData
    ? podcastEpisodesData.map((ep) => ({
        id: ep.episodeNumber ?? 0,
        title: ep.title,
        guest: ep.guest ?? "",
        duration: ep.duration ?? "",
        date: ep.publishedAt
          ? new Date(ep.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
          : "",
        audioUrl: ep.audioUrl,
      }))
    : null;

  return (
    <TTSContext.Provider value={tts}>
      <main className="min-h-screen bg-background">
        <SEO
          title="NEXTPredict"
          description="The front page of prediction markets. Real-time odds, news, and analysis across politics, crypto, finance, tech, and more."
          canonical="/"
          image="https://nextpredict.io/og-default.png"
          seoOverride={seoOverride}
          jsonLd={[
            {
              "@context": "https://schema.org",
              "@type": "WebPage",
              name: "NEXTPredict",
              description:
                "The front page of prediction markets. Real-time odds, news, and analysis across politics, crypto, finance, tech, and more.",
              url: "https://nextpredict.io",
              publisher: { "@type": "Organization", name: "NEXTPredict" },
            },
          ]}
        />
        <Navbar />

        {/* Category filter bar */}
        <section className="sticky top-16 z-40 bg-background/90 backdrop-blur-md border-b border-border">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-2 md:py-3">
            <div className="flex gap-1.5 md:gap-2 overflow-x-auto scrollbar-hide">
              {[
                "Latest",
                "Trending",
                ...sortCategories(Array.from(new Set(articlesSource.map((a) => a.category)))),
              ].map((cat) => {
                const colors = getCategoryColor(cat);
                const isActive = activeCategory === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`inline-flex items-center gap-1 md:gap-1.5 px-2.5 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium transition-all border whitespace-nowrap ${
                      isActive
                        ? `${colors.bg} ${colors.text} ${colors.border}`
                        : "bg-transparent text-muted-foreground border-border/60 hover:text-foreground hover:border-border"
                    }`}
                  >
                    {cat === "Latest" && <Clock className="w-3 h-3 md:w-3.5 md:h-3.5" />}
                    {cat === "Trending" && <Flame className="w-3 h-3 md:w-3.5 md:h-3.5" />}
                    {cat}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Ordered homepage sections */}
        {orderedSections.map((section) => {
          if (!section.is_visible) return null;
          const now = new Date();
          if (section.starts_at && new Date(section.starts_at) > now) return null;
          if (section.ends_at && new Date(section.ends_at) < now) return null;
          switch (section.section_key) {
            case "hero":
              return (
                <SectionErrorBoundary key="hero">
                  <HeroSection
                    lead={lead}
                    topStories={topStories}
                    markets={markets}
                    marketsLoading={marketsLoading}
                    articlesSource={articlesSource}
                    articlesLoading={articlesLoading}
                  />
                </SectionErrorBoundary>
              );
            case "latest_news":
              return <SectionErrorBoundary key="latest_news"><LatestNewsSection articles={articlesSource} isLoading={articlesLoading} /></SectionErrorBoundary>;
            case "trending_markets":
              return (
                <SectionErrorBoundary key="trending_markets">
                  <Suspense fallback={null}>
                    <TrendingMarketsSection markets={markets} marketsLoading={marketsLoading} />
                  </Suspense>
                </SectionErrorBoundary>
              );
            case "category_columns":
              return (
                <SectionErrorBoundary key="category_columns">
                  <Suspense fallback={null}>
                    <CategoryColumnsSection
                      categoryColumns={categoryColumns}
                      politicsMarkets={politicsMarkets}
                    />
                  </Suspense>
                </SectionErrorBoundary>
              );
            case "newsletter_banner":
              return <SectionErrorBoundary key="newsletter_banner"><Suspense fallback={null}><NewsletterSection /></Suspense></SectionErrorBoundary>;
            case "learn_section":
              return (
                <div key="learn_section">
                  {(isSectionVisible("podcast") || isSectionVisible("featured")) && (
                    <div className="max-w-7xl mx-auto px-4 md:px-6 pt-10">
                      <div className="lg:col-span-4">
                        {isSectionVisible("podcast") && (
                          <FadeInSection delay={0.2}>
                            <Suspense fallback={null}>
                              <PodcastSection episodes={sanityPodcastEpisodes} />
                            </Suspense>
                          </FadeInSection>
                        )}
                        {isSectionVisible("featured") && (
                          <FadeInSection delay={0.25}>
                            <div className={isSectionVisible("podcast") ? "mt-6" : ""}>
                              <SectionLabel label="Featured" />
                              <Link
                                to={featuredConfig?.linkUrl ?? "/summit"}
                                className="group block rounded-lg border border-border overflow-hidden"
                              >
                                {featuredConfig?.image ? (
                                  <div className="aspect-[4/3] overflow-hidden">
                                    <img
                                      src={urlFor(featuredConfig.image).width(600).height(450).url()}
                                      alt={featuredConfig.title ?? "Featured"}
                                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                    />
                                  </div>
                                ) : (
                                  <div className="aspect-[4/3] bg-gradient-to-br from-primary/20 via-primary/10 to-secondary flex flex-col items-center justify-center p-6 text-center">
                                    <span className="text-[10px] font-semibold tracking-widest uppercase text-primary mb-2">
                                      {featuredConfig?.label ?? "October 2026"}
                                    </span>
                                    <h3 className="text-lg font-extrabold text-foreground leading-snug hover-highlight transition-colors">
                                      {featuredConfig?.title ?? "NEXTPredict Summit"}
                                    </h3>
                                    <p className="text-xs text-muted-foreground mt-1.5">
                                      {featuredConfig?.subtitle ?? "New York City"}
                                    </p>
                                    <span className="inline-flex items-center gap-1 text-xs text-primary font-semibold mt-3 group-hover:underline underline-offset-4">
                                      {featuredConfig?.linkText ?? "Learn more"} <ArrowRight className="w-3 h-3" />
                                    </span>
                                  </div>
                                )}
                              </Link>
                            </div>
                          </FadeInSection>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            case "platform_section":
              return <SectionErrorBoundary key="platform_section"><Suspense fallback={null}><SummitBanner /></Suspense></SectionErrorBoundary>;
            default: {
              const config = (section as any).config;
              if (!config) return null;
              if (config.type === "richtext" && config.content) {
                return (
                  <div key={section.section_key} className="max-w-7xl mx-auto px-4 md:px-6 pt-10">
                    <FadeInSection>
                      <SectionLabel label={(section as any).label ?? section.section_key} />
                      <div className="prose prose-sm dark:prose-invert max-w-none">
                        {String(config.content)
                          .split("\n")
                          .map((line: string, i: number) => {
                            if (line.startsWith("# ")) return <h1 key={i}>{line.slice(2)}</h1>;
                            if (line.startsWith("## ")) return <h2 key={i}>{line.slice(3)}</h2>;
                            if (line.trim() === "") return <br key={i} />;
                            return <p key={i}>{line}</p>;
                          })}
                      </div>
                    </FadeInSection>
                  </div>
                );
              }
              if (config.type === "html" && config.content) {
                return (
                  <div key={section.section_key} className="max-w-7xl mx-auto px-4 md:px-6 pt-10">
                    <FadeInSection>
                      <SectionLabel label={(section as any).label ?? section.section_key} />
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(config.content)) }} />
                    </FadeInSection>
                  </div>
                );
              }
              return null;
            }
          }
        })}

        <SectionErrorBoundary>
          <Suspense fallback={null}>
            <RecommendedFeed articles={articlesSource} featureArticles={featureArticles} />
          </Suspense>
        </SectionErrorBoundary>
        <Suspense fallback={null}>
          <Footer />
        </Suspense>
      </main>
    </TTSContext.Provider>
  );
};

export default Index;
